import {state as initialState} from './state';
import {IReduxAction, IReduxChatsState, IReduxChatsStateChat} from '@src/@interfaces';
import {
    CHAT_DELETE_MESSAGE,
    CHAT_MARK_MESSAGE_AS_READ,
    CHAT_OPEN_NEW_CHAT,
    CHAT_PUSH_CHANNEL,
    CHAT_PUSH_MESSAGE,
    CHAT_RESET_LOADED_STATE,
    CHAT_SET_ACTIVE_CHAT,
    CHAT_SET_CHANNELS,
    CHAT_SET_ERROR,
    CHAT_SET_FORM_REPLY_TO,
    CHAT_SET_INIT_DATA,
    CHAT_SET_INITIALIZING,
    CHAT_SET_MESSAGES_PAGE,
    CHAT_SET_NOW_LOADING_MESSAGES_PAGE,
    CHAT_SET_PAGE,
    CHAT_SET_PAGE_CONTENT_HEIGHT,
    CHAT_SET_SCROLL_BOTTOM,
    CHAT_SET_SEARCH_TERM,
    CHAT_UPDATE_MESSAGE
} from '@src/_redux/chats/constants';
import {enrichChatChannel, getLastPageNumber, MESSAGES_PER_PAGE} from '@views/chat/components/api';
import {
    chatDeleteMessage,
    chatMarkMessageAsRead,
    chatOpenNewChat,
    chatPushMessage,
    chatSetActivePage,
    chatSetError,
    chatSetFormReplyTo,
    chatSetMessagesPage,
    chatSetNowLoadingMessagesPage,
    chatSetPageContentHeight,
    chatSetScrollBottom,
    chatUpdateMessage,
} from '@src/_redux/chats/actions';
import arrayUnique from '@src/helpers/array/arrayUnique';
import {ICommentModel} from '@src/@interfaces/models/Comment/ICommentModel';
import {parseDate, stringifyDate} from '@src/helpers/dates';
import app from '@src/helpers/app';

type TChatMessageIndex = IReduxChatsStateChat['messages']['messageIdToPositionIndex'][string | number];

function mutate<TList extends any[], TKey extends keyof TList[number]>(
    list: TList,
    key: TKey,
    value: string | number,
    mutator: (item: TList[number]) => TList[number],
): TList {
    const index = list.findIndex(item => item[key] === value);
    if (index !== -1) {
        const mutated = mutator(list[index]);

        list[index] = list[index] === mutated ? {...mutated} : mutated;
    }

    return list;
}

function makeChatPage(page: number): IReduxChatsStateChat['messages']['pages'][number] {
    return {
        page,
        loaded: false,
        isFetching: false,
        messages: [],
    };
}

function pushItemToListOfPagesToRender(list: number[], page: number): number[] {
    if (list.includes(page)) {
        return list;
    }

    return arrayUnique([...list, page]).sort((a, b) => a - b);
}

function putMessage(state: IReduxChatsState, message: ICommentModel, chatUUID: string): IReduxChatsState {
    let isNewMessage = true;

    return {
        ...state,

        chats: mutate(state.chats, 'uuid', chatUUID, chat => {
            let page = message.ordinal_number ?
                getLastPageNumber(message.ordinal_number, MESSAGES_PER_PAGE) :
                chat.lastPage;

            const isNewPage = page > chat.lastPage;

            chat.messages.pages[page] ??= {
                ...makeChatPage(page),
                loaded: isNewPage,
            };

            if (message.id != null && message.id in chat.messages.messageIdToPositionIndex) {
                const index = chat.messages.messageIdToPositionIndex[message.id];
                if (chat.messages.pages?.[index.page]?.messages?.[index.index] != null) {
                    isNewMessage = false;

                    return chat;
                }
            }

            let chatPage = chat.messages.pages[page];
            let chatPageMessagesNumber = chatPage.messages.length;

            if (isNewPage || chatPageMessagesNumber >= MESSAGES_PER_PAGE) {
                if (!isNewPage) {
                    page++;
                }

                if (chat.currentPage === (page - 1)) {
                    chat.currentPage = page;
                }

                chat.lastPage = page;
                chat.messages.pages[page] ??= {
                    ...makeChatPage(page),
                    loaded: true,
                };
                chat.pagesToRender = pushItemToListOfPagesToRender(chat.pagesToRender, page);
                chatPage = chat.messages.pages[page];
                chatPageMessagesNumber = 0;
            }

            if (message.id) {
                chat.messages.messageIdToPositionIndex[message.id] = {
                    index: chatPageMessagesNumber,
                    page,
                };
            }

            chatPage.messages = [
                ...chatPage.messages,
                chat.lastMessage = {...message, page},
            ];

            return chat;
        }),

        channels: isNewMessage ?
            mutate(state.channels, 'chatUUID', chatUUID, channel => {
                const alreadyInside = channel.messages?.find(item => item.id === message.id) != null;
                if (alreadyInside) {
                    return channel;
                }

                if (message.creator_id != null && app('auth').getUserId() !== message.creator_id) {
                    channel.unread_messages_count = 1 + (channel.unread_messages_count ?? 0);
                }
                channel.messages_count = 1 + (channel.messages_count ?? 0);
                channel.messages = [
                    message,
                    ...(channel.messages ?? []),
                ].slice(0, 5);
                channel.last_viewed_at = Date.now();
                channel.last_viewed_page = getLastPageNumber(channel.messages_count);

                return channel;
            }) :
            state.channels,
    };
}

function markMessageAsRead(
    state: IReduxChatsState,
    payload: ReturnType<typeof chatMarkMessageAsRead>['payload'],
): IReduxChatsState {
    let messageIndex = -1;
    const channelIndex = state.channels.findIndex(item => {
        if (item.messages != null) {
            messageIndex = item.messages.findIndex(item => item.id === payload.commentId);
        }

        return messageIndex !== -1;
    });

    const viewerIsCurrentUser = app('auth').getUserId();
    const readMark = {
        id: payload.markId,
        user_id: payload.viewerId,
        read_at: stringifyDate(),
        is_read: true,
    };

    let channels = [...state.channels];
    if (
        channelIndex !== -1 &&
        messageIndex !== -1 &&
        channels[channelIndex]?.messages?.[messageIndex] != null
    ) {
        channels = [...channels];
        const messages = channels[channelIndex].messages as ICommentModel[];

        messages[messageIndex] = {
            ...messages[messageIndex],
            read_marks: [
                ...(messages[messageIndex].read_marks ?? []),
                readMark,
            ],
        };

        channels[channelIndex] = {...channels[channelIndex]};
        channels[channelIndex].messages = [...messages];
        if (viewerIsCurrentUser) {
            channels[channelIndex].unread_messages_count = Math.max(
                0,
                (channels[channelIndex].unread_messages_count ?? 0) - 1
            );
        }
    }

    let chatMessageIndex: any = null;
    const chatIndex = state.chats.findIndex(item => {
        chatMessageIndex = item.messages.messageIdToPositionIndex[payload.commentId] ?? null;

        return chatMessageIndex != null;
    });

    const chats = [...state.chats];
    if (chatIndex !== -1 && chats[chatIndex].lastMessage?.id === payload.commentId) {
        chats[chatIndex].lastMessage = {
            ...(chats[chatIndex].lastMessage as ICommentModel),
            read_marks: [
                ...(chats[chatIndex].lastMessage?.read_marks ?? []),
                readMark,
            ],
        };
    }

    if (
        chatIndex !== -1 &&
        chatMessageIndex != null &&
        chats[chatIndex]
            ?.messages.pages?.[chatMessageIndex.page as number]
            .messages?.[chatMessageIndex.index as number] != null
    ) {
        const index: TChatMessageIndex = chatMessageIndex;
        const messages = chats[chatIndex].messages.pages[index.page].messages;

        chats[chatIndex].messages.pages[index.page].messages[index.index] = {
            ...messages[index.index],
            read_marks: [
                ...(messages[index.index].read_marks ?? []),
                readMark,
            ],
        };
        chats[chatIndex].messages.pages[index.page].messages = [
            ...chats[chatIndex].messages.pages[index.page].messages
        ];

        // update `last_viewed_at` and `last_viewed_page` properties of channel
        if (viewerIsCurrentUser && channelIndex !== -1 && channels[channelIndex] != null) {
            const message = chats[chatIndex].messages.pages[index.page].messages[index.index];

            if (message.created_at) {
                channels[channelIndex].last_viewed_at = Math.max(
                    channels[channelIndex].last_viewed_at,
                    +parseDate(message.created_at)
                );

            }

            if (message.ordinal_number) {
                channels[channelIndex].last_viewed_page = Math.max(
                    channels[channelIndex].last_viewed_page,
                    getLastPageNumber(message.ordinal_number)
                );
            }
        }
    }

    return {...state, channels, chats};
}

export const reducer = (state: IReduxChatsState | undefined, action: IReduxAction): Required<IReduxChatsState> => {
    state ??= initialState;

    switch (action.type) {
        case CHAT_SET_SEARCH_TERM:
            return {
                ...state,
                filter: {
                    ...state.filter,
                    query: action.payload?.searchTerm ?? null,
                },
            };

        case CHAT_RESET_LOADED_STATE:
            return {...state, loaded: false};

        case CHAT_SET_INITIALIZING:
            return {...state, ...action.payload};

        case CHAT_SET_INIT_DATA:
            return {
                ...state,
                loaded: true,
                loading: false,
                users: action.payload?.users ?? [],
                channels: action.payload?.channels ?? [],
            };

        case CHAT_SET_ERROR:
            const setErrorPayload: ReturnType<typeof chatSetError>['payload'] = action.payload;
            const errorDetails: IReduxChatsState['error'] = setErrorPayload?.error == null ? null : {
                ...state.error,
                ...setErrorPayload?.error,
                occurredAt: stringifyDate(),
            };

            if (setErrorPayload.uuid) {
                return {
                    ...state,
                    chats: mutate(
                        state.chats,
                        'uuid',
                        setErrorPayload.uuid,
                        chat => {
                            chat.error = errorDetails;
                            for (const page in chat.messages.pages) {
                                chat.messages.pages[page] = {
                                    ...chat.messages.pages[page],
                                    isFetching: false,
                                };
                            }

                            return chat;
                        }
                    ),
                };
            }

            return {...state, error: errorDetails};

        case CHAT_SET_ACTIVE_CHAT:
            return {
                ...state,
                activeChatUUID: action.payload?.uuid ?? null,
            };

        case CHAT_SET_CHANNELS:
            return {...state, channels: action.payload.channels ?? []};

        case CHAT_PUSH_CHANNEL:
            return {
                ...state,
                channels: [
                    ...state.channels,
                    enrichChatChannel(action.payload.channel, state.channels.length),
                ]
            };

        case CHAT_OPEN_NEW_CHAT:
            const newChatPayload: ReturnType<typeof chatOpenNewChat>['payload'] = action.payload;
            if (state.chats.some(item => item.uuid === newChatPayload.uuid)) {
                return state;
            }

            const activeChannel = state.channels.find(item => item.chatUUID === newChatPayload.uuid);
            const totalPages = getLastPageNumber(activeChannel?.messages_count ?? 0);

            const activePage = activeChannel?.last_viewed_page ?? totalPages;

            return {
                ...state,
                chats: [
                    ...state.chats,
                    {
                        channelId: null,
                        entity: null,
                        ...newChatPayload,

                        scrollBottom: activePage === totalPages ? 0 : null,
                        newMessagesPosition: 'bottom',
                        lastMessage: activeChannel?.messages?.[0] ?? null,
                        newMessagesNumber: activeChannel?.unread_messages_count ?? 0,
                        currentPage: activePage,
                        lastPage: Math.max(totalPages, activePage),
                        pagesToRender: [activePage],
                        error: null,
                        messages: {
                            messageIdToPositionIndex: {},
                            pages: {
                                [activePage]: makeChatPage(activePage),

                                [totalPages]: {
                                    ...makeChatPage(totalPages),
                                    messages: (activeChannel?.messages ?? []).map(message => {
                                        return {...message, page: totalPages};
                                    }).reverse(),
                                }
                            },
                        },
                        leaveMessageForm: {},
                    }
                ],
            };

        case CHAT_SET_FORM_REPLY_TO:
            const setForReplyToPayload: ReturnType<typeof chatSetFormReplyTo>['payload'] = action.payload;

            return {
                ...state,
                chats: mutate(state.chats, 'uuid', setForReplyToPayload.chatUUID, chat => {
                    return {
                        ...chat,
                        leaveMessageForm: {
                            ...chat.leaveMessageForm,
                            answerTo: setForReplyToPayload.replyTo,
                        },
                    };
                })
            };

        case CHAT_SET_NOW_LOADING_MESSAGES_PAGE:
            const setNowLoadingPayload: ReturnType<typeof chatSetNowLoadingMessagesPage>['payload'] = action.payload;

            return {
                ...state,
                chats: mutate(
                    state.chats,
                    'uuid',
                    setNowLoadingPayload.chatUUID,
                    chat => {
                        chat.messages.pages[setNowLoadingPayload.page] ??= makeChatPage(setNowLoadingPayload.page);
                        chat.messages.pages[setNowLoadingPayload.page].isFetching = true;

                        return chat;
                    }
                ),
            };

        case CHAT_SET_MESSAGES_PAGE:
            const setMessagesPagePayload: ReturnType<typeof chatSetMessagesPage>['payload'] = action.payload;

            return {
                ...state,
                chats: mutate(
                    state.chats,
                    'uuid',
                    setMessagesPagePayload.chatUUID,
                    chat => {
                        chat.messages.pages[setMessagesPagePayload.page] ??= makeChatPage(setMessagesPagePayload.page);
                        chat.pagesToRender = pushItemToListOfPagesToRender(chat.pagesToRender, setMessagesPagePayload.page);

                        const chatPage = chat.messages.pages[setMessagesPagePayload.page];

                        const lastMessage = setMessagesPagePayload.messages.at(-1);
                        if (lastMessage != null && chat.lastPage === setMessagesPagePayload.page) {
                            chat.lastMessage = lastMessage;
                        }

                        chatPage.isFetching = false;
                        chatPage.loaded = true;
                        chatPage.messages = setMessagesPagePayload.messages.map((message, index) => {
                            chat.messages.messageIdToPositionIndex[message.id] = {
                                page: setMessagesPagePayload.page,
                                index,
                            };

                            return {...message, page: setMessagesPagePayload.page};
                        });

                        return chat;
                    }
                ),
            };

        case CHAT_SET_PAGE:
            const setPagePayload: ReturnType<typeof chatSetActivePage>['payload'] = action.payload;

            return {
                ...state,
                chats: mutate(
                    state.chats,
                    'uuid',
                    setPagePayload.chatUUID,
                    chat => ({...chat, currentPage: setPagePayload.page})
                ),
            };

        case CHAT_PUSH_MESSAGE:
            const pushMessagePayload: ReturnType<typeof chatPushMessage>['payload'] = action.payload;

            return putMessage(state, pushMessagePayload.message, pushMessagePayload.chatUUID);

        case CHAT_UPDATE_MESSAGE:
            const updateMessagePayload: ReturnType<typeof chatUpdateMessage>['payload'] = action.payload;

            return putMessage(state, updateMessagePayload.message, updateMessagePayload.chatUUID);

        case CHAT_MARK_MESSAGE_AS_READ:
            return markMessageAsRead(state, action.payload);

        case CHAT_DELETE_MESSAGE:
            const deleteMessagePayload: ReturnType<typeof chatDeleteMessage>['payload'] = action.payload;

            const deletedMessageId = typeof deleteMessagePayload.message === 'object' ?
                deleteMessagePayload.message?.id :
                deleteMessagePayload.message;

            return {
                ...state,
                channels: mutate(state.channels, 'chatUUID', deleteMessagePayload.chatUUID, channel => {
                    if (channel.messages == null) {
                        return channel;
                    }

                    const index = channel.messages.findIndex(item => item.id === deletedMessageId);
                    if (index !== -1 && index in channel.messages) {
                        channel.messages[index].message = null;
                        channel.messages[index].files = [];
                        channel.messages[index].deleted_at = stringifyDate();

                        // recreate array of messages
                        channel.messages = channel.messages.slice();
                    }

                    return channel;
                }),
                chats: mutate(state.chats, 'uuid', deleteMessagePayload.chatUUID, chat => {
                    if (!(deletedMessageId in chat.messages.messageIdToPositionIndex)) {
                        return chat;
                    }

                    const index = chat.messages.messageIdToPositionIndex[deletedMessageId];
                    if (chat.messages.pages?.[index.page].messages?.[index.index] != null) {
                        chat.messages.pages[index.page].messages[index.index].message = null;
                        chat.messages.pages[index.page].messages[index.index].files = [];
                        chat.messages.pages[index.page].messages[index.index].deleted_at = stringifyDate();

                        // recreate array of messages
                        chat.messages.pages[index.page].messages = chat.messages.pages[index.page].messages.slice();
                    }

                    return chat;
                }),
            };

        case CHAT_SET_PAGE_CONTENT_HEIGHT:
            const setPageContentHeightPayload: ReturnType<typeof chatSetPageContentHeight>['payload'] = action.payload;

            return {
                ...state,
                chats: mutate(state.chats, 'uuid', setPageContentHeightPayload.chatUUID, chat => {
                    const page = setPageContentHeightPayload.page;
                    chat.messages.pages[page] = {
                        ...(chat.messages.pages[page] ?? makeChatPage(page)),
                        contentHeight: setPageContentHeightPayload.contentHeight,
                    };

                    return chat;
                }),
            };

        case CHAT_SET_SCROLL_BOTTOM:
            const setPageScrollTopPayload: ReturnType<typeof chatSetScrollBottom>['payload'] = action.payload;

            return {
                ...state,
                chats: mutate(state.chats, 'uuid', setPageScrollTopPayload.chatUUID, chat => {
                    chat.scrollBottom = setPageScrollTopPayload.scrollBottom;

                    return chat;
                }),
            };

        default:
            return state;
    }
};
